import React, { FC, Suspense, useEffect, useState } from 'react';
import { useForm, SubmitHandler, FormProvider, Controller } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import { Input } from './Input';
import { Panel } from '../Panel';
import { Spacer } from '../Spacer';
import { FormSubmissionInput } from '../../types/graphqlTypes';
import { useCreate_Block_Management_QuoteMutation } from '../../types/graphqlHelpers';
import { Textarea } from './Textarea';
import { Typography } from '../Typography';
import { Select } from './Select';
import { TITLES } from '../../const';
import Blade, { BladeBody, BladeContainer, BladeFooter } from '../Blade';

import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { SmallPrint } from './SmallPrint';
import { getFormFields } from '../../utils/getFormFields';
import { TickCircle } from '../icon/icons/TickCircle';
import { isBrowser } from '../../utils/isBrowser';
import { Loader } from '../Loader';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { RecaptchaAction } from './RecaptchaAction';
import { LinkTo } from '../LinkTo';
import { WhatsAppButton } from '../WhatsAppButton';
import { alertBadEmail, validateEmail } from '../../utils/emailBlackList';

const PhoneNumberInput = React.lazy(() => import('./PhoneNumberInput'));

const StyledTitleWrap = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.md};
  `}
`;

const StyledLead = styled(Typography)`
  ${({ theme: { space } }) => css`
    max-width: 530px;
  `}
`;

export const BlockManagementQuote: FC = () => {
  const { dataLayerPush } = useDataLayerPush();
  // Handle form submission

  // Temp var to allow mock submisisons
  const [hasSent, setHasSent] = useState(false);

  // Mutation to submit the form
  const [CreateBlockManagementQuoteMutation, { data, loading, error }] = useCreate_Block_Management_QuoteMutation();

  const formMethods = useForm({
    mode: 'onSubmit',
  });

  const { register, handleSubmit, formState, control, watch } = formMethods;
  const { errors, dirtyFields, touchedFields, isSubmitted } = formState;
  const { handleReCaptchaVerify, isHuman } = useRecaptcha({ formMethods, action: 'blockManagementQuote' });
  useEffect(() => {
    function checkSuccess() {
      if (!loading && data?.form?.createFormSubmission?.success) {
        setHasSent(true);
      }
    }
    checkSuccess();
  }, [data, dataLayerPush, loading]);

  // Function to handle collecting form data and  submitting the GQL mutation
  const onSubmit: SubmitHandler<FormSubmissionInput> = async (formData) => {
    alertBadEmail(formData.email);
    delete formData.captchaToken;
    const submitData = {
      pageUrl: isBrowser ? window.location.href : '',
      type: 'BLOCK_QUOTE',
      fromName: `${formData.forename} ${formData.surname}`,
      fromEmail: formData.email,
      fields: getFormFields(formData),
    };

    dataLayerPush({
      event: 'formSubmit',
      formName: 'blockManagementQuote',
      formValidation: 'success',
      formError: undefined,
    });

    await CreateBlockManagementQuoteMutation({
      variables: { formSubmission: submitData },
    });
  };

  // Get Block Management division data
  const { blockManagementDivisionData } = useStaticQuery(graphql`
    query blockManagementDivisionQuery {
      blockManagementDivisionData: kfhDivisionType(remoteId: { eq: "1005" }) {
        whatsApp {
          whatsAppEnabled
          whatsAppFullUrl
        }
      }
    }
  `);

  const ownPropertiesValue = watch('ownProperties');

  return (
    <Blade id="blockManagementQuote" customBlade>
      <FormProvider {...formMethods}>
        {(!isSubmitted || !hasSent) && (
          <BladeContainer>
            <BladeBody>
              <Spacer>
                <Typography variant="displaySmall">New business enquiries</Typography>

                <Typography variant="bodyLarge">
                  Nelson House
                  <br />
                  58 Wimbledon Hill Road
                  <br />
                  Wimbledon
                  <br />
                  SW19 7PA
                </Typography>

                {blockManagementDivisionData?.whatsApp?.whatsAppEnabled && (
                  <WhatsAppButton url={blockManagementDivisionData.whatsApp.whatsAppFullUrl} />
                )}

                <Typography variant="bodyLarge">
                  <a href="tel:02039936033">020 3993 6033</a>
                  <br />
                  <LinkTo to="mailto:newblockenquiry@kfh.co.uk">newblockenquiry@kfh.co.uk</LinkTo>
                </Typography>

                {/* <Typography variant="displaySmall">Request a quote</Typography> */}

                <Panel as="form" onSubmit={handleSubmit(onSubmit)} method="post" noValidate border>
                  <Spacer spacing="half">
                    <Typography component="h3" variant="titleMedium">
                      Your details
                    </Typography>
                    <Typography variant="smallprint">*Please complete all required form fields</Typography>
                    <Select
                      id="title"
                      label="Title"
                      {...register('title', { required: true })}
                      required
                      options={TITLES}
                      constrained
                      invalid={!!errors.title}
                      valid={touchedFields.title && dirtyFields.title && !errors.title}
                    />
                    <Input
                      type="text"
                      invalid={!!errors.forename}
                      valid={touchedFields.forename && dirtyFields.forename && !errors.forename}
                      label="First name"
                      id="forename"
                      placeholder="e.g Sarah"
                      {...register('forename', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      label="Last name"
                      id="surname"
                      invalid={!!errors.surname}
                      placeholder="e.g Smith"
                      valid={touchedFields.surname && dirtyFields.surname && !errors.surname}
                      {...register('surname', { required: true })}
                      required
                    />
                    <Input
                      type="email"
                      label="Email"
                      id="email"
                      invalid={!!errors.email}
                      valid={touchedFields.email && dirtyFields.email && !errors.email}
                      required
                      placeholder="e.g sarah@email.com"
                      {...register('email', {
                        validate: validateEmail, // email spam check
                        required: true,
                        pattern:
                          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      })}
                    />
                    <Controller
                      name="contactNumber"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Suspense fallback={<Loader size="small" />}>
                          <PhoneNumberInput
                            label="Phone"
                            id="PhoneNumber"
                            invalid={!!errors.contactNumber}
                            placeholder="Mobile or landline"
                            required
                            valid={touchedFields.contactNumber && dirtyFields.contactNumber && !errors.contactNumber}
                            {...field}
                          />
                        </Suspense>
                      )}
                    />
                    <Input
                      type="text"
                      invalid={!!errors.nameOfBuilding}
                      valid={touchedFields.nameOfBuilding && dirtyFields.nameOfBuilding && !errors.nameOfBuilding}
                      label="Name of building"
                      id="nameOfBuilding"
                      {...register('nameOfBuilding', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      invalid={!!errors.propertyAddress}
                      valid={touchedFields.propertyAddress && dirtyFields.propertyAddress && !errors.propertyAddress}
                      label="Property address"
                      id="propertyAddress"
                      {...register('propertyAddress', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      invalid={!!errors.propertyAddressLine2}
                      valid={
                        touchedFields.propertyAddressLine2 &&
                        dirtyFields.propertyAddressLine2 &&
                        !errors.propertyAddressLine2
                      }
                      label="Address line 2"
                      id="propertyAddressLine2"
                      {...register('propertyAddressLine2', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      invalid={!!errors.postcode}
                      valid={touchedFields.postcode && dirtyFields.postcode && !errors.postcode}
                      label="Postcode"
                      id="postcode"
                      errorMessage="Please enter a valid postcode"
                      {...register('postcode', {
                        required: true,
                        pattern: /^([A-Z][A-HJ-Y]?\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A{2})$/i,
                      })}
                      required
                    />
                    <Input
                      type="text"
                      invalid={!!errors.numberOfUnits}
                      valid={touchedFields.numberOfUnits && dirtyFields.numberOfUnits && !errors.numberOfUnits}
                      label="Number of units"
                      id="numberOfUnits"
                      {...register('numberOfUnits', { required: true })}
                      required
                    />
                    <Select
                      id="ownProperties"
                      label="Do you own all the properties in the building?"
                      {...register('ownProperties', { required: true })}
                      required
                      options={[
                        { name: 'Yes', value: 'Yes' },
                        { name: 'No', value: 'No' },
                      ]}
                      invalid={!!errors.ownProperties}
                      valid={touchedFields.ownProperties && dirtyFields.ownProperties && !errors.ownProperties}
                    />
                    {ownPropertiesValue === 'Yes' && (
                      <Controller
                        name="numberOfPropertiesLet"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Input
                            type="text"
                            invalid={!!errors.numberOfPropertiesLet}
                            valid={
                              touchedFields.numberOfPropertiesLet &&
                              dirtyFields.numberOfPropertiesLet &&
                              !errors.numberOfPropertiesLet
                            }
                            label="How many are currently let to tenants?"
                            id="numberOfPropertiesLet"
                            {...field}
                            required
                          />
                        )}
                      />
                    )}
                    <Textarea
                      invalid={!!errors.additionalComments}
                      valid={
                        touchedFields.additionalComments && dirtyFields.additionalComments && !errors.additionalComments
                      }
                      label="Additional comments and information"
                      id="additionalComments"
                      {...register('additionalComments', { required: true })}
                    />
                  </Spacer>
                </Panel>
                <RecaptchaAction show={!isHuman} handleReCaptchaVerify={handleReCaptchaVerify} />
                <SmallPrint />
              </Spacer>
            </BladeBody>
            <BladeFooter
              primaryButton={{
                label: 'Submit',
                onClick: handleSubmit(onSubmit),
                disableClickTracking: true,
              }}
            />
          </BladeContainer>
        )}

        {isSubmitted && hasSent && (
          <BladeContainer>
            <BladeBody>
              <Panel>
                <Spacer>
                  <StyledTitleWrap>
                    <TickCircle size="extraLarge" />
                    <Typography variant="displaySmall">Received</Typography>
                  </StyledTitleWrap>
                  <StyledLead variant="lead">Thank you for your submission.</StyledLead>
                </Spacer>
              </Panel>
            </BladeBody>
          </BladeContainer>
        )}
      </FormProvider>
    </Blade>
  );
};
